import type { ICellEditorParams, ICellRendererParams } from 'ag-grid-community';
import { computed } from 'vue';
import type { ICellEditorComponentParams, ICellRendererComponentParams } from './params';

/**
 * This wraps the value of a cell supplied by the AgGrid in a computed.
 * This allows us to use a vue component as a cell renderer/editor and have the value be reactive just like anything else in vue.
 * If we dont do this, then the value supplied to the vue component will lose its reactivity since aggrid just supplies the primitive value.
 * @param params The grid params object
 * @returns A computed wrapping the value of the cell
 */
export const useAgGridCellValueComputedWrapper = <TRowData, TCell = any>(
    params:
        | (ICellRendererParams & ICellRendererComponentParams<TRowData, TCell>)
        | (ICellEditorParams & ICellEditorComponentParams<TRowData, TCell>),
) => {
    return computed({
        get() {
            const valueGetter = params.valueGetter;

            // If we have a valueGetter, use that to get the value.
            return valueGetter(params.data, params.colDef?.field!);
        },
        set(newVal) {
            const valueSetter = (params as ICellEditorComponentParams<TRowData, TCell>).valueSetter;
            return valueSetter(params.data, params.colDef?.field!, newVal);
        },
    });
};
